// BUG-221: moment 로케일 SSoT — import 집약 + 앱 언어 코드 → 로케일 적용.
//
// 왜 한 곳에 모으는가 —
//   moment 는 `import 'moment/locale/xx'` 시 defineLocale() 이 내부적으로 globalLocale(name) 을
//   호출한다. 즉 **로케일 파일을 로드하는 것만으로 전역 로케일이 그 언어로 바뀐다.**
//   종전에는 app.js(7개)와 UserSettings.js(17개)가 각자 import 했고, UserSettings.js 의
//   마지막 줄이 'moment/locale/ru' 였다. UserSettings 는 app.js 에서 정적 import 라 앱 시작 시
//   무조건 평가되고, app.js 의 7개는 그 시점에 이미 로드돼 **모듈 캐시에 걸려 no-op** 이었다.
//   결과적으로 앱의 기본 moment 로케일이 러시아어로 굳어, 설정이 English 인 사용자에게도
//   fromNow() 가 '2 месяца назад' 로 표시됐다.
//
//   import 를 이 모듈 한 곳에 모으고 평가 마지막에 명시적으로 적용해, **import 순서와
//   무관하게** 기본값을 고정한다. 다른 모듈에서 'moment/locale/*' 를 직접 import 하면
//   같은 결함이 재발하므로 금지한다(app/UserSettings 테스트가 이를 가드한다).

import moment from 'moment';
import { resolveCurrentLangCode } from './currentLang';

// en 은 moment 내장이라 import 하지 않는다. 아래 18개가 LANG_TO_MOMENT 의 en 외 전 항목과
// 1:1 대응한다 — 누락되면 moment.locale(tag) 가 조용히 실패하고 직전 로케일이 유지된다
// (BUG-221 부수 결함: kaz 가 어디에서도 import 되지 않아 무음 폴백 상태였다).
import 'moment/locale/ko';
import 'moment/locale/ja';
import 'moment/locale/zh-cn';
import 'moment/locale/zh-tw';
import 'moment/locale/de';
import 'moment/locale/es-us';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/sk';
import 'moment/locale/pl';
import 'moment/locale/ro';
import 'moment/locale/cs';
import 'moment/locale/pt';
import 'moment/locale/sv';
import 'moment/locale/el';
import 'moment/locale/tr';
import 'moment/locale/ru';
import 'moment/locale/kk';

export const DEFAULT_MOMENT_LOCALE = 'en';

// 앱 언어 코드(ISO 639-3 계열) → moment 로케일 태그.
//
// langResources 의 LANG_TO_BCP47 과 목적은 같으나 **값이 다르다** — moment 는 'zh-Hans' /
// 'zh-Hant' / 'es' 를 모른다(각각 'zh-cn' / 'zh-tw' / 'es-us' 로 제공). 재사용하면 세 언어가
// 조용히 미해석돼 직전 로케일이 유지되므로 별도 맵이 필요하다. 중복이 아니라 별개 계약이다.
//
// eng='en' 은 미국 영어다(IMP-438, 사용자 확정 2026-08-11). 'en-gb' 로 되돌리면 일/월 순서가
// LANG_TO_BCP47.eng='en' 과 반대가 되어 같은 화면에 3/9 와 09/03 이 섞인다.
//
// 목록은 langResources 의 SUPPORTED_LANGS 19종과 반드시 일치해야 한다(드리프트 감지 테스트 있음).
export const LANG_TO_MOMENT = {
    eng: 'en', kor: 'ko', jpn: 'ja', cmn: 'zh-cn', zhh: 'zh-tw', deu: 'de',
    spa: 'es-us', fre: 'fr', ita: 'it', slk: 'sk', pol: 'pl', ron: 'ro', ces: 'cs',
    por: 'pt', swe: 'sv', ell: 'el', tur: 'tr', rus: 'ru', kaz: 'kk',
};

// 코드당 1회만 warn — fromNow() 는 렌더마다 호출되므로 제한이 없으면 콘솔이 스팸으로 덮인다
// (langResources 의 warnedCodes 와 같은 이유, Set 은 사유가 다르므로 분리).
const warnedCodes = new Set();
const warnedTags = new Set();

// 앱 언어 코드로 moment 전역 로케일을 적용하고, 실제 적용된 태그를 반환한다.
// 미지원·미해석 코드는 반드시 'en' 을 적용한다 — 그냥 두면 직전 로케일이 남아 BUG-221 의
// '설정과 무관한 언어로 표시' 가 그대로 재현된다.
export const applyMomentLocale = (code) => {
    // Object.prototype 체인 조회 차단. 'constructor'/'toString'/'__proto__' 등이 들어오면
    // 상속 값(함수/객체)이 truthy 로 반환돼 화이트리스트와 warn 을 모두 우회하고,
    // moment.locale() 에 넘어가 조용히 무시된다.
    const tag = Object.prototype.hasOwnProperty.call(LANG_TO_MOMENT, code)
        ? LANG_TO_MOMENT[code]
        : null;

    if (!tag) {
        // 언어 미설정(null/undefined/'')은 **정상 상태**다 — 로그인 전이거나 언어를 고르지
        // 않은 사용자. currentLang.resolveCurrentLangCode() 도 같은 null 을 정상 반환한다.
        // 여기서 warn 하면 코드당 1회 제한을 두고 지키려던 관측 신호가 정상 노이즈로 오염된다.
        if (code !== null && code !== undefined && code !== '' && !warnedCodes.has(code)) {
            warnedCodes.add(code);
            console.warn('[momentLocale] 미지원 언어 코드 — en 으로 폴백', {
                handler: 'applyMomentLocale',
                code,
                fallback: DEFAULT_MOMENT_LOCALE,
                reason: 'LANG_TO_MOMENT 에 없는 코드',
            });
        }
        moment.locale(DEFAULT_MOMENT_LOCALE);
        return DEFAULT_MOMENT_LOCALE;
    }

    // moment.locale(tag) 는 **실제 적용된** 로케일명을 반환한다. 로케일 데이터가 로드되지
    // 않았으면 전역 로케일을 바꾸지 않고 직전 값을 그대로 돌려준다 — 무음 실패다.
    // 위 import 목록에서 한 줄이 빠지는 회귀를 여기서 잡아 warn + en 폴백으로 되돌린다.
    const applied = moment.locale(tag);
    if (applied !== tag) {
        if (!warnedTags.has(tag)) {
            warnedTags.add(tag);
            console.warn('[momentLocale] 로케일 데이터 미로드 — en 으로 폴백', {
                handler: 'applyMomentLocale',
                code,
                tag,
                applied,
                reason: "moment/locale/* import 누락 — 이 모듈 상단 import 목록 확인",
            });
        }
        moment.locale(DEFAULT_MOMENT_LOCALE);
        return DEFAULT_MOMENT_LOCALE;
    }
    return applied;
};

// 저장된 사용자 언어로 전역 로케일을 맞춘다. 해석은 currentLang 의 resolveCurrentLangCode()
// 하나만 쓴다 — UI 텍스트(Translate.js)와 **같은 함수**라 상대시간과 화면 언어가 어긋나지
// 않는다. 종전 app.js 는 rguardSettingLanguage 만 봐서 둘이 갈라질 수 있었다.
export const syncMomentLocaleFromStorage = () => applyMomentLocale(resolveCurrentLangCode());

// 모듈 평가 시점에 즉시 적용한다 — 두 가지를 동시에 해결한다.
//   1) 위 import 들이 남긴 전역 로케일('kk')을 사용자 설정 값으로 덮어써, import 순서에
//      기본값이 좌우되지 않게 한다.
//   2) app.js 의 setLocale() 은 useEffect(…, []) 라 **첫 렌더 이후**에 실행되는데,
//      moment.locale() 변경은 리렌더를 유발하지 않는다. 그때 적용하면 이미 그려진
//      fromNow() 가 기본 로케일로 남는다(BUG-221 원인 2).
syncMomentLocaleFromStorage();
